import type React from 'react';
import { useMemo } from 'react';

import type { ColumnDef } from '@tanstack/react-table';
import type { PermissionTag } from '@vlabs/api/artemy/model/PermissionTag';
import type { PermissionTagRecord } from '@vlabs/api/artemy/model/PermissionTagRecord';
import { useHasPermission, usePageAccess } from '@vlabs/permissions';
import { dateFormatter } from '@vlabs/uikit/components/table-view/cell/formatters/date';
import { useTranslation } from 'react-i18next';

import { DeletePermissionTagsIconButton } from '@vlabs/monokit/features/delete-permission-tags';
import { GoToPermissionTagIconButton } from '@vlabs/monokit/features/go-to-permission-tag';
import { TableView } from '@vlabs/monokit/shared/ui/table-view';

import {
  renderHeader,
  permissionTagCell,
  renderCell,
  createdByUserCell,
  createTimeCell,
  groupCell,
  actionCell,
  dividerCell,
} from './cells/cells';
import { CreatedByUserCell } from './cells/CreatedByUserCell';
import { GroupCell } from './cells/GroupCell';
import { PermissionTagCell } from './cells/PermissionTagCell';

interface PermissionTagListTableProps {
  data: PermissionTagRecord[];
}

function PermissionTagListTable({
  data,
}: PermissionTagListTableProps): React.JSX.Element {
  const { t } = useTranslation();
  const canDeleteTag = useHasPermission('permission_tags', 'delete');
  const canViewGroups = useHasPermission('group', 'read');
  const canViewTagDetails = usePageAccess('permissions_tags_details');

  const columns: ColumnDef<PermissionTagRecord>[] = useMemo(() => ([
    {
      accessorKey: 'permission_tag',
      header: renderHeader(permissionTagCell, t('widgets:permission_tag_list_table_viewer.permission_tag_list_table.permission_tag')),
      cell: renderCell<PermissionTagRecord, PermissionTag>(permissionTagCell, (ctx) => (
        <PermissionTagCell permissionTag={ctx.getValue()} />
      )),
    },
    {
      accessorKey: 'created_by_user',
      header: renderHeader(createdByUserCell, t('widgets:permission_tag_list_table_viewer.permission_tag_list_table.created_by_user')),
      cell: renderCell<PermissionTagRecord, PermissionTag>(createdByUserCell, (ctx) => (
        <CreatedByUserCell userId={ctx.getValue()} />
      )),
    },
    {
      accessorKey: 'create_time',
      header: renderHeader(createTimeCell, t('widgets:permission_tag_list_table_viewer.permission_tag_list_table.create_time')),
      cell: renderCell<PermissionTagRecord, PermissionTag>(createTimeCell, (ctx) => dateFormatter(ctx.getValue())),
    },
    ...(canViewGroups ? [{
      accessorKey: 'permission_tag' as const,
      id: 'streamGroups',
      header: renderHeader(groupCell, t('widgets:permission_tag_list_table_viewer.permission_tag_list_table.groups')),
      cell: renderCell<PermissionTagRecord, PermissionTag>(groupCell, (ctx) => (
        <GroupCell permissionTag={ctx.getValue()} />
      )),
    }] : []),
    ...(canViewTagDetails ? [{
      accessorKey: 'permission_tag' as const,
      id: 'goToPermissionTag',
      header: renderHeader(actionCell),
      cell: renderCell<PermissionTagRecord, PermissionTag>(actionCell, (ctx) => (
        <GoToPermissionTagIconButton permissionTag={ctx.getValue()} />
      )),
    }] : []),
    ...(canDeleteTag ? [{
      accessorKey: 'permission_tag' as const,
      id: 'deletePermissionTag',
      header: renderHeader(actionCell),
      cell: renderCell<PermissionTagRecord, PermissionTag>(actionCell, (ctx) => (
        <DeletePermissionTagsIconButton
          data-testid={ctx.row.original.permission_tag}
          permissionTags={[ctx.getValue()]}
        />
      )),
    }] : []),
    { cell: dividerCell(undefined), id: 'divider' },
  ] as ColumnDef<PermissionTagRecord>[]), [t, canDeleteTag, canViewGroups, canViewTagDetails]);

  return (
    <TableView
      columns={columns}
      data={data}
    />
  );
}

export { PermissionTagListTable };
